import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import { UserService } from '../user/user.service';
import { BehaviorSubject, interval, Observable, Subject, tap } from 'rxjs';
import { AuthService } from '../auth/auth.service';
import { Wallet, WalletInterface, WalletMapper } from 'src/app/model/Wallet';
import { AppConstants } from 'src/app/constants/app-constants';
import { User } from 'src/app/model/User';
import { AbstractApiService } from '../abstract-api-service/abstract-api-service';
import {
  USER_WALLETS_ENDPOINT,
  USER_WALLET_DEFAULT_ENDPOINT,
  USER_WALLET_DELETE_ENDPOINT,
  USER_WALLET_UPDATE_ENDPOINT,
} from 'src/app/constants/api-routes';

export interface ShouldBeWalletInterface {
  abanumber: string;
  accountNumber: string;
  accountType: number;
  achwalletID: string;
  active: number;
  content: string | null;
  nickname: string;
  operation?: string;
  validated: number;
}

export interface WalletOperation {
  operation: string;
  accountType: number;
  nickname: string;
  accountNumber: string;
  abaNumber: string;
  validated: string;
  active: string;
}

export interface DeleteResponse {
  message: string;
  response: string;
}

@Injectable({
  providedIn: 'root',
})
export class WalletService extends AbstractApiService<Wallet> {
  /** Injected services */
  private _userService: UserService = inject(UserService);
  private _authService: AuthService = inject(AuthService);
  protected _http: HttpClient = inject(HttpClient);

  /** Observable that emits the latest list of wallets */
  protected _subject = new Subject<Wallet[]>();
  public wallets$ = this._subject.asObservable();

  /** Data collection for wallets */
  protected _collection: Wallet[] | undefined = undefined;

  /** An observable to refresh API data */
  public timer: Observable<number> = interval(AppConstants.API_FETCH_INTERVAL);
  private _timers: number[] = [0];

  /** How long it has been since the last refresh */
  private _timeSinceLastRefresh = new BehaviorSubject<string>('0:00');
  public timeSinceLastRefresh$ = this._timeSinceLastRefresh.asObservable();

  /**
   * Default constructor for the service, used to initialize services
   */
  constructor() {
    super();

    // On authentication changes, clear persisted data
    this._userService.user$.subscribe((user: User | null) => {
      if (this._authService.authenticated && user != undefined) {
        this._fetch();
      } else {
        this._clear();
      }
    });

    // Continuously query the backend to receive new wallets
    this._startTimer(0);
    this.timer.subscribe((x: number) => {
      this._timers.splice(this._timers.indexOf(x));
      this._startTimer(x + 1);
      this._fetch();
    });
  }

  /**
   * Get the current collection of wallets
   *
   * @return A reference to the current collection of wallets, or undefined if not fetched yet
   */
  public getWallets(): Wallet[] | undefined {
    return this._collection?.map((x) => x.copyOf());
  }

  /**
   * Legacy method for backward compatibility - returns observable
   * @deprecated Use wallets$ observable instead
   */
  public getWallet(): Observable<Wallet[]> {
    return this.wallets$;
  }

  /**
   * Helper function to set current wallet collection
   *
   * @param wallets
   */
  private _setWallets(wallets: Wallet[]): void {
    this._collection = wallets.map((x) => x.copyOf());
    this._subject.next(wallets.map((x) => x.copyOf()));
  }

  /**
   * Helper function to clear current wallet collection
   */
  private _clear(): void {
    this._collection = undefined;
    this._subject.next([]);
  }

  /**
   * Get wallets from the backend. You should subscribe to the 'wallets$'
   * field to receive updates
   */
  private _fetch(): void {
    // Make sure the user is authenticated
    if (!this._authService.authenticated) return;

    // Get the user information
    const user = this._userService.user;
    if (!user || !user.id) return;

    // Get company id
    const companyId = this._userService.company?.companyId;
    if (!companyId) return;

    const userId = user.id;
    const endpointUrl = USER_WALLETS_ENDPOINT(companyId, userId);
    console.log(`[INFO]: Fetching wallets via endpoint: ${endpointUrl}`);

    this._http.get<WalletInterface[]>(endpointUrl).subscribe({
      next: (walletInterfaces: WalletInterface[]): void => {
        if (walletInterfaces == undefined) {
          console.error(`[ERR]: cannot cast backend output into wallet list`);
          return;
        }

        // Convert the interfaces to Wallet instances
        let wallets: Wallet[] = [];
        for (let walletInterface of walletInterfaces) {
          wallets.push(WalletMapper.toModel(walletInterface));
        }

        // Update the stored wallets and notify subscribers
        this._setWallets(wallets);
        console.log(`[INFO]: successfully queried wallets`);
      },
      error: (err: any) => {
        console.error(`[ERR]: Failed to fetch wallets (${err})`);
      },
    });
  }

  /**
   * Start the timer for tracking time since last refresh
   */
  private async _startTimer(index: number) {
    this._timers.push(index);

    let seconds = 0;
    let minutes = 0;

    while (this._timers.includes(index)) {
      // Increment seconds
      await delay(1000);
      seconds++;

      // Increment minutes
      if (seconds >= 60) {
        seconds = 0;
        minutes++;
      }

      // Update timer
      this._timeSinceLastRefresh.next(
        `${minutes}:${('' + seconds).length === 1 ? '0' : ''}${seconds}`,
      );
    }
  }

  /**
   * Adds a new wallet for the authenticated user and updates the local collection
   */
  public addWallet$(walletData: WalletOperation): Observable<any> {
    // Check authenticated status
    if (!this._authService.authenticated) {
      throw new Error('User not authenticated');
    }

    // Get user id
    const userId = this._userService.user?.id;
    if (!userId) {
      throw new Error('User information is missing');
    }

    // Get company id
    const companyId = this._userService.company?.companyId;
    if (!companyId) {
      throw new Error('Company information is missing');
    }

    // Make backend request
    const endpointUrl = USER_WALLETS_ENDPOINT(companyId, userId);
    return this._http.post<any>(endpointUrl, walletData).pipe(
      tap((_: any) => {
        if (!walletData.nickname.includes('[p]')) {
          walletData.nickname = walletData.nickname + ' [p]';
        }

        // We get back a 200 nls status, which is useless. So, we add the wallet data to a new pending wallet, which gets added to the collection
        const newWallet = new Wallet(
          '', // achwalletID is unknown until next fetch
          walletData.nickname,
          walletData.accountNumber,
          walletData.accountType,
          walletData.abaNumber,
          '', // bankName is unknown
          walletData.validated,
          walletData.active,
        );
        this.addWalletToCollection(newWallet);
      }),
    );
  }

  /**
   * Set a wallet as the default wallet
   * @param achWalletId The ACH wallet ID to set as default
   * @returns An observable that emits the response from the backend
   */
  public setDefaultWallet$(
    achWalletId: string,
  ): Observable<ShouldBeWalletInterface> {
    const userId = this._userService.user?.id;
    if (!userId) throw new Error('User ID is missing');

    const companyId = this._userService.company?.companyId;
    if (!companyId) throw new Error('Company information is missing');

    const url = USER_WALLET_DEFAULT_ENDPOINT(companyId, userId, achWalletId);
    return this._http.put<ShouldBeWalletInterface>(url, {}).pipe(
      tap((resp: ShouldBeWalletInterface) => {
        // Update all wallets to be disabled
        this._collection?.forEach((w) => (w.active = '0'));

        // Find the wallet in the collection and update its status
        const walletToSetDefault = this._collection?.find(
          (w) => w.achwalletID === achWalletId,
        );
        if (walletToSetDefault) {
          const updatedWallet = new Wallet(
            walletToSetDefault.achwalletID,
            resp.nickname,
            resp.accountNumber,
            resp.accountType,
            resp.abanumber,
            walletToSetDefault.bankName,
            '' + resp.validated,
            '' + resp.active,
          );
          this.updateWalletInCollection(updatedWallet);
        }
      }),
    );
  }

  /**
   * Delete a wallet by its ACH wallet ID and update the local collection
   *
   * @param achWalletId The ACH wallet ID of the wallet to delete
   * @returns An observable that emits the delete response from the backend
   */
  public deleteWallet$(achWalletId: string): Observable<DeleteResponse> {
    const userId = this._userService.user?.id;
    if (!userId) {
      throw new Error('User ID is missing');
    }

    const companyId = this._userService.company?.companyId;
    if (!companyId) {
      throw new Error('Company information is missing');
    }

    const url = USER_WALLET_DELETE_ENDPOINT(companyId, userId, achWalletId);
    return this._http.post<DeleteResponse>(url, {}).pipe(
      tap((resp: DeleteResponse) => {
        // Find the wallet in the collection and remove it
        const walletToDelete = this._collection?.find(
          (w) => w.achwalletID === achWalletId,
        );
        if (walletToDelete) {
          this.removeWalletFromCollection(walletToDelete);
        }
      }),
    );
  }

  public updateWallet$(
    achWalletId: string,
    walletData: WalletOperation,
  ): Observable<ShouldBeWalletInterface> {
    // Check authenticated status
    if (!this._authService.authenticated) {
      throw new Error('User not authenticated');
    }

    // Get user id
    const userId = this._userService.user?.id;
    if (!userId) {
      throw new Error('User information is missing');
    }

    // Get company id
    const companyId = this._userService.company?.companyId;
    if (!companyId) {
      throw new Error('Company information is missing');
    }

    // Make backend request
    const endpointUrl = USER_WALLET_UPDATE_ENDPOINT(
      companyId,
      userId,
      achWalletId,
    );
    return this._http
      .put<ShouldBeWalletInterface>(endpointUrl, walletData)
      .pipe(
        tap((response: ShouldBeWalletInterface) => {
          // Find the existing wallet in the collection to preserve all fields
          const existingWallet = this._collection?.find(
            (w) => w.achwalletID === achWalletId,
          );
          if (existingWallet) {
            // Create updated wallet by merging existing data with updates
            const updatedWallet = new Wallet(
              existingWallet.achwalletID,
              response.nickname,
              response.accountNumber,
              response.accountType,
              response.abanumber,
              existingWallet.bankName,
              '' + response.validated,
              '' + response.active,
            );
            this.updateWalletInCollection(updatedWallet);
          }
        }),
      );
  }

  /**
   * Add a wallet to the local collection after successful API call
   *
   * @param newWallet The wallet to add to the collection
   */
  public addWalletToCollection(newWallet: Wallet): void {
    this.refresh(newWallet, 'create');
  }

  /**
   * Update a wallet in the local collection after successful API call
   *
   * @param updatedWallet The wallet to update in the collection
   */
  public updateWalletInCollection(updatedWallet: Wallet): void {
    this.refresh(updatedWallet, 'modify');
  }

  /**
   * Remove a wallet from the local collection after successful API call
   *
   * @param walletToDelete The wallet to remove from the collection
   */
  public removeWalletFromCollection(walletToDelete: Wallet): void {
    this.refresh(walletToDelete, 'delete');
  }
}

// Helper function to delay for a given number of milliseconds
const delay = (delay: number): Promise<void> => {
  return new Promise((r) => {
    setTimeout(r, delay);
  });
};
